import { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, User, BookOpen, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface TutoringSchedule {
  id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  notes: string | null;
  status: string;
  instructor_id?: string;
}

interface EnhancedCalendarProps {
  userId: string;
  userType: 'instructor' | 'student' | 'parent';
  children?: Array<{ student_email: string; student_name: string }>;
}

export default function EnhancedCalendar({ userId, userType, children = [] }: EnhancedCalendarProps) {
  const [schedules, setSchedules] = useState<TutoringSchedule[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<TutoringSchedule | null>(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    loadSchedules();
  }, [userId, userType, children]);

  const loadSchedules = async () => {
    try {
      let query = supabase
        .from('tutoring_schedules')
        .select('*')
        .order('session_date', { ascending: true });

      if (userType === 'instructor') {
        query = query.eq('instructor_id', userId);
      } else if (userType === 'student') {
        query = query.eq('student_email', userId); // For students, we pass their email
      } else if (userType === 'parent' && children.length > 0) {
        const childEmails = children.map(child => child.student_email);
        query = query.in('student_email', childEmails);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSchedules(data || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'default';
      case 'completed': return 'secondary';
      case 'cancelled': return 'destructive';
      case 'rescheduled': return 'outline';
      default: return 'default';
    }
  };

  const deleteSchedule = async (scheduleId: string) => {
    if (userType !== 'instructor') return;
    
    try {
      const { error } = await supabase
        .from('tutoring_schedules')
        .delete()
        .eq('id', scheduleId);

      if (error) throw error;

      setSchedules(schedules.filter(s => s.id !== scheduleId));
      setShowEventDialog(false);
      toast.success('Schedule deleted successfully');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  // Convert schedules to FullCalendar events
  const calendarEvents = schedules.map(schedule => ({
    id: schedule.id,
    title: userType === 'instructor' 
      ? (schedule.student_name || schedule.student_email)
      : schedule.subject,
    start: `${schedule.session_date}T${schedule.start_time}`,
    end: `${schedule.session_date}T${schedule.end_time}`,
    backgroundColor: schedule.status === 'completed' ? '#22c55e' : 
                    schedule.status === 'cancelled' ? '#ef4444' : 
                    schedule.status === 'rescheduled' ? '#f59e0b' : '#3b82f6',
    borderColor: 'transparent',
    extendedProps: {
      schedule: schedule
    }
  }));

  const handleEventClick = (clickInfo: any) => {
    const schedule = clickInfo.event.extendedProps.schedule;
    setSelectedEvent(schedule);
    setShowEventDialog(true);
  };

  const handleDateSelect = (selectInfo: any) => {
    if (userType === 'instructor') {
      // Navigate to create schedule with pre-selected date
      const selectedDate = selectInfo.start.toISOString().split('T')[0];
      window.location.href = `/instructor/tutoring/create?date=${selectedDate}`;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              {userType === 'instructor' && 'My Tutoring Schedule'}
              {userType === 'student' && 'My Tutoring Sessions'}  
              {userType === 'parent' && "Children's Tutoring Sessions"}
            </span>
            {userType === 'instructor' && (
              <Button asChild>
                <Link to="/instructor/tutoring/create">Add Session</Link>
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Loading calendar...</p>
            </div>
          ) : (
            <div className="calendar-container">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={calendarEvents}
                eventClick={handleEventClick}
                selectable={userType === 'instructor'}
                select={handleDateSelect}
                height="auto"
                aspectRatio={1.8}
                dayMaxEvents={3}
                moreLinkClick="popover"
                eventDisplay="block"
                displayEventTime={true}
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
                slotLabelFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Details Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Session Details</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-lg">
                  {userType === 'instructor' 
                    ? (selectedEvent.student_name || selectedEvent.student_email)
                    : selectedEvent.subject
                  }
                </h3>
                <Badge variant={getStatusColor(selectedEvent.status)} className="mt-1">
                  {selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1)}
                </Badge>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{selectedEvent.subject}</span>
                </div>

                {selectedEvent.topic && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Topic: {selectedEvent.topic}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {format(new Date(selectedEvent.session_date), 'MMM dd, yyyy')} • {' '}
                    {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}
                  </span>
                </div>

                {userType === 'parent' && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{selectedEvent.student_name}</span>
                  </div>
                )}

                {selectedEvent.notes && (
                  <div className="mt-3">
                    <p className="text-sm font-medium mb-1">Notes:</p>
                    <p className="text-sm text-muted-foreground">{selectedEvent.notes}</p>
                  </div>
                )}
              </div>

              {userType === 'instructor' && (
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" size="sm" asChild className="flex-1">
                    <Link to={`/instructor/tutoring/edit/${selectedEvent.id}`}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Link>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => deleteSchedule(selectedEvent.id)}
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        .calendar-container .fc {
          font-family: inherit;
        }
        
        .calendar-container .fc-toolbar-title {
          font-size: 1.125rem;
          font-weight: 600;
        }
        
        .calendar-container .fc-button {
          background: hsl(var(--primary));
          border-color: hsl(var(--primary));
          font-size: 0.875rem;
        }
        
        .calendar-container .fc-button:hover:not(:disabled) {
          background: hsl(var(--primary)) !important;
          border-color: hsl(var(--primary)) !important;
          opacity: 0.9;
        }
        
        .calendar-container .fc-button:focus {
          box-shadow: 0 0 0 0.2rem hsl(var(--primary) / 0.25);
        }
        
        .calendar-container .fc-event {
          border-radius: 4px;
          padding: 2px 4px;
          font-size: 0.75rem;
          cursor: pointer;
        }
        
        .calendar-container .fc-day-today {
          background-color: hsl(var(--primary) / 0.05) !important;
        }
        
        .calendar-container .fc-daygrid-event {
          margin-bottom: 1px;
        }
      `}</style>
    </div>
  );
}